<script setup lang="ts" generic="K extends string, T extends ITabElement<K>">
import { useRouteQuery } from "@vueuse/router";
import { onMounted } from "vue";

import { ITabElement } from "@/components/ui/tabs/tabs.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

interface IProps {
  elements: Array<T>;
  preload?: boolean;
  defaultTab?: T["key"];
}

interface IEmits {
  (e: "item-clicked", item: T): void;
}

defineSlots();

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const { rawT } = useTypedIntl();

const routeQuery = useRouteQuery("tab");

function handleClick(item: T) {
  if (item.disabled === true) {
    return;
  }

  routeQuery.value = item.key;
  emits("item-clicked", item);
}

onMounted(() => {
  if (!routeQuery.value) {
    routeQuery.value = props.elements.find((el) => el.disabled !== true)?.key ?? props.elements[0]?.key;
    return;
  }
});
</script>

<template>
  <ul
    class="tabs"
    :data-test-id="DATA_TEST_IDS.components.tabs.component"
  >
    <li
      v-for="item in props.elements"
      :key="item.key"
    >
      <a
        class="tab-item"
        :class="{
          active: routeQuery === item.key,
          disabled: item.disabled === true,
          'cursor-pointer': item.disabled !== true,
        }"
        :data-test-id="DATA_TEST_IDS.components.tabs.item"
        role="button"
        @click="handleClick(item)"
      >
        <span>
          {{ rawT(item.label) }}
        </span>
      </a>
    </li>
  </ul>

  <template
    v-for="item in props.elements"
    :key="item.key"
  >
    <KeepAlive v-if="props.preload">
      <div v-show="item.key === routeQuery">
        <slot
          :name="item.key"
          v-bind="item"
        />
      </div>
    </KeepAlive>
    <KeepAlive v-else>
      <div v-if="item.key === routeQuery">
        <slot
          :name="item.key"
          v-bind="item"
        />
      </div>
    </KeepAlive>
  </template>
</template>

<style scoped lang="scss">
.tabs {
  @apply w-full flex flex-row items-center border-b;

  .tab-item {
    @apply w-full flex items-center justify-center pb-4 px-4 border-b-4;

    * {
      @apply text-text-secondary;
    }

    &:not(:hover) {
      @apply border-b-transparent;
    }

    &:hover > * {
      @apply text-text-primary;
    }

    &.active {
      @apply border-b-4 border-brand;

      * {
        @apply font-semibold text-text-primary;
      }
    }
  }
}
</style>
